import type { Attachment } from '@medplum/fhirtypes'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { useCallback } from 'react'
import { toast } from 'sonner'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'

export interface UseRetryAttachmentConversionResult {
  /**
   * Fires the retry mutation for a single attachment. Resolves once the patch
   * settles. Errors surface via a toast and the returned promise rejects so
   * callers can opt into additional handling.
   */
  retry: (attachment: Attachment) => Promise<void>
  /** True while the mutation is in flight. */
  isRetrying: boolean
  /**
   * True once the docRef has loaded from cache/network, so callers can decide
   * whether to render the retry affordance. Always true when no id was given.
   */
  isReady: boolean
}

/**
 * Returns a callback that flips the `attachment-conversion` extension on a
 * single attachment back to `pending` and PATCHes the parent
 * DocumentReference. The Medplum subscription dispatches on every
 * DocumentReference write, so re-arming the extension is enough to re-run the
 * server-side conversion handler — no separate "kick" endpoint required.
 *
 * The polling already on `DocumentReferenceDisplay` picks up the new
 * `pending` state and refreshes the modal as the conversion completes.
 *
 * @param documentReferenceId - ID of the parent DocumentReference. When
 *   `undefined`, the hook is a no-op (returns `isReady: true`).
 * @returns Object exposing `retry`, `isRetrying`, and `isReady`.
 */
export function useRetryAttachmentConversion(
  documentReferenceId: string | undefined
): UseRetryAttachmentConversionResult {
  const { data: docRef, isLoading } = useRead(
    ExtendedDocumentReference,
    documentReferenceId
  )
  const patch = usePatchMutation<ExtendedDocumentReference>()
  const { mutateAsync: patchDocument } = patch

  const retry = useCallback(
    async (attachment: Attachment): Promise<void> => {
      if (!docRef) {
        toast.error('Document not loaded yet — please try again in a moment')
        return
      }

      // Identify the target by hash (preferred) or url. We deliberately avoid
      // index-based matching from the call site: the `Attachment` we receive
      // may have a synthetic React-key id appended by the
      // `ExtendedDocumentReference.attachments` getter, and we want to match
      // against the canonical `content` array directly.
      const targetIndex = docRef.attachments.findIndex((a) => {
        if (attachment.hash && a.hash) {
          return attachment.hash === a.hash
        }
        return a.url === attachment.url
      })

      if (targetIndex === -1) {
        toast.error('Could not locate attachment on document')
        return
      }

      try {
        await patchDocument({
          instance: docRef,
          mutateFn(draft) {
            draft.retryAttachmentConversionAtIndex(targetIndex)
          },
        })
      } catch (err) {
        console.error('Failed to retry attachment conversion', err)
        toast.error('Failed to retry conversion — please try again')
        throw err
      }
    },
    [docRef, patchDocument]
  )

  return {
    retry,
    isRetrying: patch.isPending,
    isReady: !documentReferenceId || !isLoading,
  }
}
